import axios from "axios";
import {
    GET_ORDER_TYPE,
    CREATE_ORDER_URL,
    SEND_ORDER_RECEIPT,
    GET_BANNER_DETAILS,
    RECEIPT_BASE_URL,
    GET_TABLE_ORDERS,
} from "./constant";
import { store } from "../index";
import { order_place, clear_last_order, set_last_order, clear_applied_offer } from "../store/actions/action";
import { clearCart } from "../store/actions/cartActions";
import { get_customer_list } from "./customer_helper";
import { refreshCalculationJs } from "@cyntra/calculation-engine";

// ─────────────────────────────────────────────
// LOW-LEVEL API UTILITIES
// ─────────────────────────────────────────────

const groupBy = (xs, key) =>
    xs.reduce((rv, x) => { (rv[x[key]] = rv[x[key]] || []).push(x); return rv; }, {});

const optionAxios = {
    headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        "x-api-source": "webapp"
    },
};

const getSession = () => store.getState().sessionReducer || {};

const getAuthOptions = () => {
    const { token } = getSession();
    return { ...optionAxios, headers: { ...optionAxios.headers, authorization: token || "" } };
};

const handleApiError = (error, fallback = null) => {
    if (error?.response?.status === 401) window.location.href = "/rescan";
    return fallback;
};

const postApi = async (url, data) => {
    try {
        const response = await axios.post(url, data, getAuthOptions());
        return response.data;
    } catch (error) {
        const status = error?.response?.status;
        // 400 / 429: backend sends a meaningful body (e.g. "Order is already closed").
        // Return it tagged with _httpStatus so callers can read the message and act.
        if (status === 400 || status === 429) {
            return { ...(error.response.data || {}), _httpStatus: status, status: false };
        }
        return handleApiError(error, null);
    }
};

// ─────────────────────────────────────────────
// EXPOSED API ENDPOINTS
// ─────────────────────────────────────────────



const create_order = async (input) => {
    const { store_id, location_id, table_details, is_fsr, store_details } = getSession();
    const reduxCart = store.getState().cartReducer || {};
    const orderType = reduxCart?.orderType?.order;
    const payload = { ...input, store_id, location_id: location_id || null, table_details: table_details || {} };

    if (!payload.order_type && orderType) {
        payload.order_type = orderType;
    }
    if (store_details?.integration) {
        payload.integration = store_details.integration;
    }
    // Preserve caller-supplied store_details (e.g. CLOSE payload for DI needs
    // order_code + floor/table info). Otherwise drop the raw session
    // store_details that would leak restaurant meta into the request.
    if (!input?.store_details) {
        delete payload.store_details;
    }
    if (is_fsr) payload.is_send = true;
    return await postApi(CREATE_ORDER_URL, payload);
};

// Builds a payment_details entry for a CLOSE save-order call, given the
// gateway response and the tender the user picked. Kept here so all
// close-order call sites produce the same shape.
const buildPaymentDetailsForClose = (transResponse, selectedTender, orderObj) => {
    const transResp = transResponse || {};
    const tender    = selectedTender || {};
    
    const amount    = orderObj?.transaction_grand_amount ?? transResp.amount ?? 0;

    const transId   = transResp.razorpay_payment_id || transResp.TXNID || transResp.transaction_id || "";
    const gatewayBrand = tender.payment_gateway
        || (transResp.razorpay_payment_id ? "RAZORPAY"
            : transResp.TXNID ? "PAYTM"
            : "NONE");

    return [{
        _id:      tender._id      || "",
        tnd_type: tender.tnd_type || "",
        tnd_code: tender.tnd_code || "",
        tnd_des:  tender.tnd_des  || "",
        source:   "QR",
        amount,
        round_off_amount:    orderObj?.round_off_amount || 0,
        transaction_id:      transId,
        payment_gateway:     gatewayBrand,
        transaction_details: {
            razorpay_payment_id: transResp.razorpay_payment_id || "",
            razorpay_order_id:   transResp.razorpay_order_id   || "",
            razorpay_signature:  transResp.razorpay_signature  || "",
            ...(transResp.TXNID && {
                TXNID:  transResp.TXNID,
                STATUS: transResp.STATUS || "",
            }),
        },
    }];
};

// Close an existing order (KOT / running FSR) by re-saving it via save-order.
// Recalculates via the engine so round-off applies for the chosen tender,
// then attaches payment_details + round_off_details and posts to save-order.
const closeOrderViaSaveOrder = async ({ order, transResponse, orderStatus = "CLOSE" }) => {
    const state         = store.getState();
    const sessionReducer = state.sessionReducer || {};
    const reduxCart      = state.cartReducer    || {};
    const storeConfig    = sessionReducer.store_config || {};
    const selectedTender = reduxCart?.selectedTender || {};

    const recalculated = (order && (selectedTender?.tnd_code || selectedTender?.tnd_type))
        ? (executeCalculation(order, selectedTender, storeConfig) || order)
        : order;

    const gc = storeConfig?.general_config || {};
    const round_off_details = {
        round_off:     gc.round_off === true || gc.round_off === "true",
        type:          gc.type || gc.round_off_type || "",
        payment_types: gc.payment_types || gc.round_off_payments || [],
    };

    // Force order_type to the canonical Redux object so CLOSE mirrors OPEN.
    // Backend uses order_code (e.g. "DI") to trigger table-lock / vacate.
    let orderTypeObj = reduxCart?.orderType?.order
        || (typeof recalculated?.order_type === "object" ? recalculated.order_type : null);

    // For Dine-in, also send store_details with order_code + floor/table info
    // so backend can acquire the table lock and vacate on CLOSE.
    const orderCode      = orderTypeObj?.order_code || "";
    const sessionStore   = sessionReducer.store_details || {};
    const tableDetails   = sessionReducer.table_details || {};

    const storeDetailsForClose = (orderCode === "DI") ? {
        store_id:    sessionStore.store_id || sessionReducer.store_id || "",
        order_code:  "DI",
        order_des:   orderTypeObj?.order_des || orderTypeObj?.order_type || "Dine in",
        floor_id:    tableDetails.floor_id   || "",
        floor_no:    tableDetails.floor_no   || "",
        floor_name:  tableDetails.floor_name || "",
        table_id:    tableDetails.table_id   || "",
        table_no:    tableDetails.table_no   || tableDetails.table_name || "",
        ...(sessionStore.integration && { integration: sessionStore.integration }),
    } : undefined;

    const payload = {
        ...recalculated,
        ...(orderTypeObj && { order_type: orderTypeObj }),
        ...(storeDetailsForClose && { store_details: storeDetailsForClose }),
        order_status:   orderStatus,
        payment_details: buildPaymentDetailsForClose(transResponse, selectedTender, recalculated),
        round_off_details,
    };

    return await create_order(payload);
};


const get_table_orders = async (input) => {
    return await postApi(GET_TABLE_ORDERS, input);
};

const get_order_type = async ({ token, store_id } = {}) => {
    console.log("[get_order_type] called", { has_token: !!token, store_id });
    if (!token || !store_id) {
        console.log("[get_order_type] EXIT — missing token or store_id");
        return null;
    }
    try {
        const response = await axios.post(GET_ORDER_TYPE, { store_id }, {
            ...optionAxios,
            headers: { ...optionAxios.headers, authorization: token }
        });
        console.log("[get_order_type] response", {
            status: response?.status,
            data_length: response?.data?.data?.length,
            sample: response?.data?.data?.[0],
        });
        return response?.data?.data;
    } catch (error) {
        console.error("[get_order_type] error", {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
        });
        return handleApiError(error, null);
    }
};

const send_order_receipt = async (input) => {
    const { store_id } = getSession();
    const res = await postApi(SEND_ORDER_RECEIPT, { ...input, store_id });
    return res?.data;
};

const download_invoice = async (_id) => {
    try {
        const URL = RECEIPT_BASE_URL.replace(":id", _id._id);
        const response = await axios.get(URL, { ...getAuthOptions(), responseType: "blob" });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${_id?.orderNo?.toString()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        if (error?.response?.status === 401) handleApiError(error);
        else console.error("Download failed", error);
    }
};


const get_banner_detial = async () => {
    const { store_id } = getSession();
    const res = await postApi(GET_BANNER_DETAILS, { store_id });
    return res?.data;
};

// ─────────────────────────────────────────────
// PAYLOAD BUILDERS — each has a single purpose
// ─────────────────────────────────────────────

/**
 * Transform raw cart items into the flat item_details array the API expects.
 * - Sequences items and addons
 * - Renames selected_addons_items → selectedAddonsItems (canonical name)
 * - Applies offline_invoice_no to every item and addon
 */
// startSeq: if the produced flat list is being appended to an existing flat list
// (e.g. KOT adding items to a previously saved order), pass the max item_seq of
// the existing list so the new items continue the numbering instead of starting at 1.
export const transformItems = (combinedItems, startSeq = 0, opts = {}) => {
    const { preserveDiscount = false } = opts;
    let seq = startSeq;
    const flat = [];

    for (const item of combinedItems) {
        seq += 1;
        if (item.is_void || item.isVoid || item.is_refund || item.main_item_id) continue;
        const mainSeq = seq;

        const variantName = item.variant_name || item.item_variant?.[0]?.variant_name || "regular";
        const skuVal     = item.item_variant?.[0]?.sku || "";
        const basePrice  = parseFloat(item.regular_sales_unit_price ?? item.item_variant?.[0]?.plu ?? 0);

        // Accept either name from the incoming item
        const addonsSource = item.selectedAddonsItems || item.selected_addons_items || [];
        const parentQty = parseInt(item.quantity || item.qty || 1, 10) || 1;
        // Two shapes per addon:
        //   - nested (mappedAddons): lives in parent.selectedAddonsItems, qty:1.
        //     Engine's calculateItemPrice overwrites its subTotal from the flat
        //     sibling's total_gross_sale (CalculationEngine-sharedLogic.mjs line
        //     22521-22522), so the initial subTotal here is a placeholder.
        //   - flat sibling (mappedAddonsFlat): pushed into the outer item_details
        //     with quantity:parentQty so a2y computes total_gross_sale =
        //     extended_amount * parentQty. That value is what gets folded back
        //     into the parent's line via the nested addon's subTotal.
        // Matches backend save-order-v2 shape (nested qty:1, sibling quantity:parentQty).
        const mappedAddons = [];
        const mappedAddonsFlat = [];
        addonsSource.forEach((addon) => {
            seq += 1;
            const addonSeq = seq;
            const addonPrice = parseFloat(
                addon.regular_sales_unit_price
                ?? addon.price
                ?? addon.item_variant?.[0]?.plu
                ?? 0
            );

            const cleanAddon = { ...addon };
            delete cleanAddon.taxAmount;
            delete cleanAddon.total_item_tax;
            delete cleanAddon.total_net_sale;
            delete cleanAddon.sub_total;
            delete cleanAddon.total_gross_sale;
            delete cleanAddon.taxable_amount;
            if (!preserveDiscount) {
                delete cleanAddon.discount_amount;
                delete cleanAddon.total_discount;
                // Legacy cartReducer DISCOUNT action stamps `.discount` on items —
                // engineDiscount is authoritative now, so strip it before handing
                // items to the engine to prevent the stale stamp from poisoning
                // the calc when engineDiscount is cleared and we fall through here.
                delete cleanAddon.discount;
            }
            if (Array.isArray(cleanAddon.tax)) {
                cleanAddon.tax = cleanAddon.tax.map(t => {
                    const cleanTax = { ...t };
                    delete cleanTax.tax_amount;
                    delete cleanTax.taxable_amount;
                    return cleanTax;
                });
            }

            const commonAddonFields = {
                ...cleanAddon,
                item_seq:                 addonSeq,
                item_id:                  addon.item_variant?.[0]?.item_id || "",
                sku:                      addon.item_variant?.[0]?.sku || "",
                // Hoist variant_name from the selected variant so downstream
                // renders can distinguish addons that share item_name but differ
                // by variant (e.g. Jain Thali — Mild / Spicy / Very Spicy).
                variant_name:             addon.variant_name || addon.item_variant?.[0]?.variant_name || "",
                main_item_id:             item.item_variant?.[0]?.item_id || item.item_id || "",
                item_confirmed:           true,
                is_void:                  false,
                isVoid:                   false,
                is_refund:                false,
                is_prepared:              false,
                is_kot_printed:           false,
                regular_sales_unit_price: addonPrice,
                extended_amount:          addonPrice,
            };

            // Nested shape (goes under parent.selectedAddonsItems): qty stays 1.
            // Engine reads price/subTotal/qty (SelectedAddonsItems deserializer at
            // CalculationEngine-sharedLogic.mjs line 4377-4380). subTotal is
            // written back by calculateItemPrice from the sibling's gross.
            mappedAddons.push({
                ...commonAddonFields,
                quantity: 1,
                qty:      1,
                price:    addonPrice,
                subTotal: addonPrice,
            });

            // Flat sibling row (goes into item_details): quantity = parentQty so
            // a2y computes gross = addonPrice * parentQty.
            mappedAddonsFlat.push({
                ...commonAddonFields,
                quantity: parentQty,
                qty:      parentQty,
                price:    addonPrice,
                subTotal: addonPrice,
            });
        });

        const cleanItem = { ...item };
        delete cleanItem.selected_addons_items; // always use camelCase going forward
        delete cleanItem.taxAmount;
        delete cleanItem.total_item_tax;
        delete cleanItem.total_net_sale;
        delete cleanItem.sub_total;
        delete cleanItem.total_gross_sale;
        delete cleanItem.taxable_amount;
        if (!preserveDiscount) {
            delete cleanItem.discount_amount;
            delete cleanItem.total_discount;
            // See addon comment above — same reason.
            delete cleanItem.discount;
        }
        if (Array.isArray(cleanItem.tax)) {
            cleanItem.tax = cleanItem.tax.map(t => {
                const cleanTax = { ...t };
                delete cleanTax.tax_amount;
                delete cleanTax.taxable_amount;
                return cleanTax;
            });
        }

        // Build selected_modifier from the chosen variant's modifier_varaint
        // + the item's modifiers_group source data. Preserve any pre-built
        // selected_modifier already on the incoming item (e.g. re-hydrated
        // orders).
        const selectedModVariants = item.item_variant?.[0]?.modifier_varaint || [];
        const modifierGroups      = item.modifiers_group || [];
        let builtSelectedModifier = [];
        if (Array.isArray(selectedModVariants) && selectedModVariants.length && Array.isArray(modifierGroups) && modifierGroups.length) {
            for (const mod of selectedModVariants) {
                const mgData = modifierGroups.find(g => g?.mg_id === mod?.mg_id);
                const mvData = mgData?.item_modifier?.find(m => m?.mv_id === mod?.mv_id);
                if (mgData && mvData) {
                    const { item_modifier, ...rest } = mgData;
                    builtSelectedModifier.push({
                        ...rest,
                        modifiers: [{ ...mvData, isSelected: true }],
                    });
                }
            }
        }
        const selectedModifier = item.selected_modifier?.length
            ? item.selected_modifier
            : builtSelectedModifier;
        delete cleanItem.selected_modifier;

        flat.push({
            ...cleanItem,
            item_seq:                 mainSeq,
            selectedAddonsItems:      mappedAddons,
            selected_modifier:        selectedModifier,
            variant_name:             variantName,
            sku:                      skuVal,
            item_id:                  item.item_variant?.[0]?.item_id || "",
            regular_sales_unit_price: basePrice,
            // Engine's Kotlinx deserializer needs a numeric Int for `quantity`;
            // a string here silently falls back to the constructor default of 1
            // (see ItemDetailsList line 3083 in the engine .mjs), so sub_total
            // ends up computed as extended_amount × 1 instead of × quantity.
            quantity:                 parseInt(item.quantity || item.qty || 1, 10) || 1,
            item_confirmed:           true,
            is_void:                  Boolean(item.is_void || item.isVoid),
            isVoid:                   Boolean(item.isVoid  || item.is_void),
            is_refund:                Boolean(item.is_refund),
            is_prepared:              false,
            is_kot_printed:           false,
            main_item_id:             item.main_item_id || "",
            extended_amount:          parseFloat(item.extended_amount || basePrice || 0),
            specialRequest:           item.specialRequest || item.special_request || item.instruction || item.item_note || "",
        });

        // Also push addons into the flat list (API requires a flat array).
        // Use the sibling shape here (quantity = parentQty) so the engine's
        // a2y computes total_gross_sale = addonPrice * parentQty for the
        // sibling row, which is then folded back into the parent line via
        // the nested addon's subTotal.
        mappedAddonsFlat.forEach(addon => flat.push(addon));
    }

    return flat;
};

/** Sum sub_total and tax from a flat items list (skip addons and voided items) */
const sumItemTotals = (items) => {
    let subTotal = 0;
    let tax      = 0;
    items.forEach(item => {
        if (item.is_void || item.isVoid || item.is_refund || item.main_item_id) return;
        subTotal += parseFloat(item.sub_total || item.total_net_sale || 0);
        if (Array.isArray(item.tax) && item.tax.length > 0) {
            item.tax.forEach(t => { tax += parseFloat(t.tax_amount || t.amount || 0); });
        } else {
            tax += parseFloat(item.total_item_tax || item.taxAmount || 0);
        }
    });
    return {
        subTotal: parseFloat(subTotal.toFixed(2)),
        tax:      parseFloat(tax.toFixed(2)),
    };
};

/** Build the store-level charges array */
const buildCharges = (flatItems, StoreCharge) => {
    if (!StoreCharge) return [];
    const byStore        = groupBy(flatItems, "store_id");
    const storeCount     = Object.keys(byStore).length || 1;
    const perStoreCharge = StoreCharge.value / storeCount;
    let chargeTax        = 0;
    StoreCharge.charge_tax_ids?.forEach(t => { chargeTax += (t.tax_pct / 100) * perStoreCharge; });
    const totalCharge    = perStoreCharge + chargeTax;

    return Object.keys(byStore).map(sid => ({
        ...StoreCharge,
        store_id:                  sid,
        order_level_charges:       parseFloat(perStoreCharge.toFixed(2)),
        order_level_charges_tax:   parseFloat(chargeTax.toFixed(2)),
        total_order_level_charges: parseFloat(totalCharge.toFixed(2)),
    }));
};

/** Aggregate taxes from the flat items list */
const buildTaxes = (flatItems, storeId) => {
    const map = {};
    flatItems.forEach(item => {
        if (!Array.isArray(item.tax)) return;
        item.tax.forEach(t => {
            const id = t._id || t.tax_id;
            if (!id) return;
            if (!map[id]) {
                map[id] = {
                    _id:            id,
                    store_id:       storeId,
                    tax_dsc:        t.tax_dsc,
                    tax_pct:        t.tax_pct,
                    action:         t.action || "C",
                    created_at:     t.created_at,
                    status:         t.status !== undefined ? t.status : true,
                    tax_type:       t.tax_type || "all",
                    updated_at:     t.updated_at,
                    amount:         0,
                    tax_amount:     0,
                    taxable_amount: 0,
                };
            }
            map[id].amount         += parseFloat(t.amount || t.tax_amount || 0);
            map[id].tax_amount     += parseFloat(t.tax_amount || t.amount  || 0);
            map[id].taxable_amount += parseFloat(item.sub_total || 0);
        });
    });
    return Object.values(map).map(t => ({
        ...t,
        amount:         parseFloat(t.amount.toFixed(2)),
        tax_amount:     parseFloat(t.tax_amount.toFixed(2)),
        taxable_amount: parseFloat(t.taxable_amount.toFixed(2)),
    }));
};

/** Build payment_details array */
const buildPaymentDetails = ({ paymentResp, selectedTender, subTotal, tax, tip, charges, taxes, transaction_grand_amount }) => {
    const transId = paymentResp?.razorpay_payment_id || paymentResp?.TXNID || paymentResp?.transaction_id || selectedTender?.transaction_id || "";
    const gatewayBrand = paymentResp?.razorpay_payment_id ? "RAZORPAY" : (paymentResp?.TXNID ? "PAYTM" : (selectedTender?.payment_gateway || "NONE"));

    const transactionDetails = {
        transaction_id: transId,
        _id: "",
        razorpay_payment_id: paymentResp?.razorpay_payment_id || "",
        razorpay_order_id: paymentResp?.razorpay_order_id || "",
        razorpay_signature: paymentResp?.razorpay_signature || "",
        ...(paymentResp?.TXNID && {
            TXNID: paymentResp.TXNID,
            STATUS: paymentResp.STATUS || ""
        })
    };

    const grandAmount = transaction_grand_amount ?? (subTotal + tax + tip);

    const built = [{
        ...(paymentResp    || {}),
        ...(selectedTender || {}),
        transaction_id:           transId,
        payment_gateway:          gatewayBrand,
        transaction_details:      transactionDetails,
        sub_total:                subTotal,
        total_item_tax:           tax,
        total_net_sale:           subTotal,
        total_gross_sale:         subTotal + tax,
        transaction_grand_amount: grandAmount,
        rounded_total:            grandAmount,
        amount:                   grandAmount,
        round_off_amount:         0,
        ...(charges.length > 0 && { charges }),
        ...(taxes.length   > 0 && { taxes }),
    }];

    console.log("[buildPaymentDetails IN]", {
        subTotal, tax, tip,
        transaction_grand_amount_arg: transaction_grand_amount,
        resolved_grandAmount:         grandAmount,
        paymentResp_amount:           paymentResp?.amount,
        paymentResp_grand:            paymentResp?.transaction_grand_amount,
        selectedTender_amount:        selectedTender?.amount,
    });
    console.log("[buildPaymentDetails OUT]", built[0]);

    return built;
};

/** Build customer_details array */
const buildCustomerDetails = (customer, customerMembership, customer_membership) => {
    if (!customer || !Object.keys(customer).length) return [{ cust_first_name: "Guest", cust_mobile: "9999999999" }];
    return [{
        ...customer,
        customer_membership,
        membership: customerMembership?.length > 0 ? customerMembership[0] : null,
    }];
};

/**
 * Run the calculation engine once.
 * Preserves custom fields (selectedAddonsItems, offline_invoice_no, etc.)
 * that the engine would otherwise strip.
 */
function executeCalculation(payload, selectedTender, storeConfig) {
    try {
        const gc = storeConfig?.general_config || {};
        const isRoundOff     = gc.round_off === true || gc.round_off === "true";
        const roundOffType   = (gc.type || gc.round_off_type || "nearest").toLowerCase();
        const paymentType    = selectedTender?.tnd_code || selectedTender?.tnd_type || "";
        const applicablePaymentTypes = Array.isArray(gc.payment_types)
            ? gc.payment_types
            : Array.isArray(gc.round_off_payments) ? gc.round_off_payments : [];
        const isTaxInclusive = gc.item_price_contains_tax === "Include";

        // Engine requires order_type as a string
        const forEngine = {
            ...payload,
            order_type: typeof payload.order_type === "object"
                ? (payload.order_type?.order_type || payload.order_type?.order_code || "Dine In")
                : (payload.order_type || "Dine In"),
        };
        // Preserve engine-stamped discount fields when the incoming payload
        // already carries an offer (applied_discount populated). Stripping
        // discount_amount/total_discount and re-running refreshCalculation
        // without offer context would zero the discount — a naked recompute
        // has no way to reconstruct it. For carts with no offer, keep the
        // original strip so the engine computes taxes cleanly.
        const hasAppliedDiscount = Array.isArray(payload.applied_discount) && payload.applied_discount.length > 0;
        if (Array.isArray(forEngine.item_details)) {
            forEngine.item_details = forEngine.item_details.map(item => {
                const cleaned = { ...item };
                delete cleaned.taxAmount;
                delete cleaned.total_item_tax;
                delete cleaned.total_net_sale;
                delete cleaned.sub_total;
                delete cleaned.total_gross_sale;
                delete cleaned.taxable_amount;
                if (!hasAppliedDiscount) {
                    delete cleaned.discount_amount;
                    delete cleaned.total_discount;
                }
                if (Array.isArray(cleaned.tax)) {
                    cleaned.tax = cleaned.tax.map(t => {
                        const cleanTax = { ...t };
                        delete cleanTax.tax_amount;
                        delete cleanTax.taxable_amount;
                        return cleanTax;
                    });
                }
                return cleaned;
            });
        }
        // Engine expects invoice_no to be numeric. Drop if empty/non-numeric.
        if (forEngine.invoice_no === "" || forEngine.invoice_no == null || isNaN(Number(forEngine.invoice_no))) {
            delete forEngine.invoice_no;
        }
        if (forEngine.offline_invoice_no === "" || forEngine.offline_invoice_no == null) {
            delete forEngine.offline_invoice_no;
        }


        console.log("[engine IN]", {
            paymentType, isRoundOff, roundOffType, applicablePaymentTypes, isTaxInclusive,
            payload: JSON.parse(JSON.stringify(forEngine)),
        });
        const calculatedStr = refreshCalculationJs(
            JSON.stringify(forEngine),
            false, 0, 0,
            paymentType,
            isRoundOff, roundOffType, applicablePaymentTypes,
            false, isTaxInclusive
        );

        if (calculatedStr) {
            const parsed = JSON.parse(calculatedStr);
            console.log("[engine OUT]", {
                sub_total: parsed.sub_total,
                total_item_tax: parsed.total_item_tax,
                total_gross_sale: parsed.total_gross_sale,
                transaction_grand_amount: parsed.transaction_grand_amount,
                round_off_amount: parsed.round_off_amount,
                items: parsed.item_details?.map(i => ({
                    name: i.item_name,
                    qty: i.quantity,
                    unit: i.regular_sales_unit_price,
                    ext: i.extended_amount,
                    sub_total: i.sub_total,
                    tax: i.total_item_tax,
                    gross: i.total_gross_sale,
                })),
            });
            // Merge engine output back into original items to preserve custom fields
            if (Array.isArray(parsed.item_details) && Array.isArray(payload.item_details)) {
                parsed.item_details = parsed.item_details.map((engineItem, idx) => {
                    const original = (engineItem.item_seq !== undefined)
                        ? payload.item_details.find(o => o.item_seq === engineItem.item_seq)
                        : payload.item_details[idx];
                    if (!original) return engineItem;
                    const merged = { ...original, ...engineItem };
                    // Normalise addon key
                    const addons = merged.selectedAddonsItems || merged.selected_addons_items;
                    if (addons) {
                        merged.selectedAddonsItems = addons;
                        delete merged.selected_addons_items;
                    }
                    return merged;
                });
            }
            return {
                ...payload,
                ...parsed,
                order_type:         payload.order_type, // restore object form
                order_state:        payload.order_state || parsed.order_state || "PLACED",
                order_status:       payload.order_status || parsed.order_status || "OPEN",
                offline_invoice_no: payload.offline_invoice_no || parsed.offline_invoice_no || "",
                invoice_no:         payload.invoice_no || parsed.invoice_no || "",
                order_no:           payload.order_no || parsed.order_no || "",
                customer_details:   payload.customer_details || parsed.customer_details || [],
                store_details:      payload.store_details || parsed.store_details || {},
                table_details:      payload.table_details || parsed.table_details || {},
            };
        }
    } catch (err) {
        console.error("CalculationEngine error:", err, err?.stack);
    }
    return payload;
};

/**
 * Compute display totals for the checkout UI.
 * Runs the calculation engine (round-off aware) and returns the raw engine result.
 * Callers read whatever fields they need (sub_total, total_item_tax, round_off_amount,
 * transaction_grand_amount, taxes, etc.).
 */
const computeCheckoutTotals = (addedItems, {
    orderType, storeId, paymentOption, storeConfig,
    itemsAreFlat = false, tip = 0, applyCurrentOffer = false,
} = {}) => {
    const tipAmount = parseFloat(tip || 0) || 0;

    // Opt-in: when the caller wants totals that reflect the currently-applied
    // offer, replay the engine's post-offer snapshot through refreshCalculation
    // with the fresh tip. The library folds tip + stamped discount into
    // transaction_grand_amount — no manual arithmetic on our side.
    if (applyCurrentOffer) {
        const engineDiscount = store.getState().cartReducer?.engineDiscount;
        if (engineDiscount?.item_details?.length) {
            const gc = storeConfig?.general_config || {};
            const isRoundOffEnabled = gc.round_off === true || gc.round_off === "true";
            const roundOffType = (gc.type || gc.round_off_type || "nearest").toLowerCase();
            const applicableRoundOffPaymentTypes = Array.isArray(gc.payment_types)
                ? gc.payment_types
                : Array.isArray(gc.round_off_payments) ? gc.round_off_payments : [];
            const selectedPaymentType = paymentOption?.tnd_code || paymentOption?.tnd_type || "";
            const isTaxInclusive = gc.item_price_contains_tax === "Include";
            // Zero the accumulator fields so refreshCalculation rebuilds them
            // from scratch. y2x (CalculationEngine-sharedLogic.mjs line 22545)
            // does `order.v2p_1 = order.v2p_1 + item.n2m_1` per item — if we
            // seed v2p_1 with the previous total_discount, replay doubles it.
            // discount_amount/discount_percent MUST be preserved: a2y (line
            // 1670-1677) uses them to distribute per-item discount for
            // order-level offers.
            const withTip = {
                ...engineDiscount,
                order_type: orderType || engineDiscount.order_type,
                store_id:   storeId,
                tip_amount: tipAmount,
                charges_data: Array.isArray(engineDiscount.charges_data) ? engineDiscount.charges_data : [],
                total_discount: 0,
                sub_total: 0,
                total_item_tax: 0,
                total_gross_sale: 0,
                total_net_sale: 0,
                transaction_grand_amount: 0,
            };
            if (withTip.invoice_no === "" || withTip.invoice_no == null || isNaN(Number(withTip.invoice_no))) {
                delete withTip.invoice_no;
            }
            if (withTip.offline_invoice_no === "" || withTip.offline_invoice_no == null) {
                delete withTip.offline_invoice_no;
            }
            try {
                console.log("[replay IN] snapshot", {
                    sub_total: engineDiscount.sub_total,
                    total_item_tax: engineDiscount.total_item_tax,
                    total_discount: engineDiscount.total_discount,
                    transaction_grand_amount: engineDiscount.transaction_grand_amount,
                    applied_discount: engineDiscount.applied_discount,
                    tip_amount: tipAmount,
                    items: engineDiscount.item_details?.map(i => ({
                        name: i.item_name,
                        qty: i.quantity,
                        unit: i.regular_sales_unit_price,
                        ext: i.extended_amount,
                        sub_total: i.sub_total,
                        gross: i.total_gross_sale,
                        net: i.total_net_sale,
                        tax: i.total_item_tax,
                        discount_amount: i.discount_amount,
                    })),
                });
                console.log("[replay IN] engineDiscount full", JSON.parse(JSON.stringify(engineDiscount || {})));
                console.log("[replay IN] withTip full (sent to refreshCalculationJs)", JSON.parse(JSON.stringify(withTip || {})));
                console.log("[replay IN] applied_discount full", JSON.parse(JSON.stringify(engineDiscount.applied_discount || [])));
                console.log("[replay IN] items full", (engineDiscount.item_details || []).map(i => JSON.parse(JSON.stringify(i))));
                const recalcStr = refreshCalculationJs(
                    JSON.stringify(withTip),
                    false, 0, 0,
                    selectedPaymentType,
                    isRoundOffEnabled, roundOffType, applicableRoundOffPaymentTypes,
                    false, isTaxInclusive,
                );
                if (recalcStr) {
                    const parsed = JSON.parse(recalcStr);
                    console.log("[replay OUT]", {
                        sub_total: parsed.sub_total,
                        total_item_tax: parsed.total_item_tax,
                        total_discount: parsed.total_discount,
                        transaction_grand_amount: parsed.transaction_grand_amount,
                        applied_discount: parsed.applied_discount,
                    });
                    console.log("[replay OUT] full parsed", JSON.parse(JSON.stringify(parsed || {})));
                    console.log("[replay OUT] applied_discount full", JSON.parse(JSON.stringify(parsed.applied_discount || [])));
                    console.log("[replay OUT] items full", (parsed.item_details || []).map(i => JSON.parse(JSON.stringify(i))));
                    return parsed;
                }
            } catch (e) {
                console.warn("[computeCheckoutTotals] offer replay failed, falling back to raw calc", e);
            }
        }
    }

    const flatItems = itemsAreFlat
        ? (addedItems || [])
        : transformItems(addedItems || []);
    const enginePayload = {
        item_details: flatItems,
        order_type:   orderType,
        store_id:     storeId,
        // Fold tip into the engine payload so transaction_grand_amount
        // reflects it (engine adds tipAmount at CalculationEngine-sharedLogic
        // line 2156).
        tip_amount:   tipAmount,
    };
    return executeCalculation(enginePayload, paymentOption, storeConfig) || {};
};

// ─────────────────────────────────────────────
// SAVE-ORDER PAYLOAD BUILDER
// Builds the calculated payload for /save-order from current Redux state.
// Used by PaymentAck after PhonePe payment success.
// payment_details is intentionally omitted — backend attaches it in save-order.
// ─────────────────────────────────────────────

const buildSaveOrderPayload = ({ activeOrder = null, orderStatus = "CLOSE" } = {}) => {
    const state         = store.getState();
    const reduxSession  = state.sessionReducer  || {};
    const reduxCart     = state.cartReducer     || {};
    const reduxCustomer = state.customerReducer || {};

    const storeInfo    = reduxSession.store_details;
    const storeConfig  = reduxSession.store_config;
    const storeId      = reduxSession.store_id;
    const tableDetails = reduxSession.table_details;
    const customer     = reduxCustomer.customer || {};
    const customerMembership = reduxCustomer.customerMembership || [];

    const isVoided = activeOrder?.is_void === true ||
                     activeOrder?.isVoid === true ||
                     (activeOrder?.order_status || "").toUpperCase() === "VOID";
    const isFSRActive = !!activeOrder && !isVoided;
    const sourceItems = isFSRActive
        ? (activeOrder?.item_details || [])
        : (reduxCart?.addedItems || []);

    const offlineNo = (isFSRActive
        ? (activeOrder?.offline_invoice_no || activeOrder?.offlineInvoiceNo)
        : reduxCart?.offline_invoice_no)
        || `QR-1-${Date.now()}`;

    const invoiceNo = isVoided
        ? ""
        : (activeOrder?.invoice_no
            || activeOrder?.invoiceNo
            || "");

    const orderNo = isVoided
        ? ""
        : (activeOrder?.order_no
            || activeOrder?.orderNo
            || "");

    // Prefer engine-stamped item_details (post-offer, per-item discount_amount +
    // applied_discount populated) so save-order carries the applied offer. Falls
    // back to raw addedItems / transformItems when no offer is applied.
    // FSR path uses activeOrder items untouched.
    const engineDiscount        = reduxCart?.engineDiscount;
    const engineHasOffer        = !isFSRActive
        && engineDiscount
        && Array.isArray(engineDiscount.item_details)
        && engineDiscount.item_details.length > 0
        && (engineDiscount.total_discount || 0) > 0;
    const flatItems = isFSRActive
        ? sourceItems
        : (engineHasOffer ? engineDiscount.item_details : transformItems(sourceItems));
    const { subTotal, tax } = sumItemTotals(flatItems);
    const tip               = reduxCart?.tip || 0;
    const StoreCharge       = storeConfig?.general_config?.store_charges || reduxCustomer?.storeCharges || null;
    const charges           = buildCharges(flatItems, StoreCharge);
    const selectedTender    = reduxCart?.selectedTender || {};

    const customerDetails = buildCustomerDetails(customer, customerMembership, {});

    const order = reduxCart?.orderType?.order;

    const gc                 = storeConfig?.general_config || {};
    const configRoundOff     = gc.round_off === true || gc.round_off === "true";
    const configType         = (gc.type || gc.round_off_type || "nearest").toLowerCase();
    const configPaymentTypes = gc.payment_types || gc.round_off_payments || [];

    const childStoreId = flatItems.find(i => i.store_id)?.store_id || reduxCart?.child_store_id || null;

    // When an offer is applied, seed totals/discount from engineDiscount so
    // executeCalculation (which now preserves per-item discount when
    // applied_discount is present) recomputes taxes on the discounted base
    // without dropping the offer. Otherwise fall back to raw sums.
    const offerTotalDiscount = engineHasOffer
        ? parseFloat((engineDiscount.total_discount || 0).toFixed(2))
        : parseFloat((reduxCart?.resultDis || 0).toFixed(2));
    const offerAppliedDiscount = engineHasOffer
        ? (engineDiscount.applied_discount || [])
        : [];
    const offerSubTotal = engineHasOffer && engineDiscount.sub_total != null
        ? parseFloat(engineDiscount.sub_total)
        : subTotal;
    const offerTax = engineHasOffer && engineDiscount.total_item_tax != null
        ? parseFloat(engineDiscount.total_item_tax)
        : tax;
    const offerGrand = engineHasOffer && engineDiscount.transaction_grand_amount != null
        ? parseFloat(engineDiscount.transaction_grand_amount) + tip
        : subTotal + tax + tip;

    const initialPayload = {
        tent_id:                  reduxSession.tent_id || "",
        order_source_code:        "QR",
        order_source:             "QR Order",
        order_type:               order,
        order_status:             orderStatus,
        order_state:              activeOrder?.order_state || "PLACED",
        emp_no:                   storeInfo?.emp_no   || "",
        store_id:                 storeInfo?.store_id || storeId,
        ...(childStoreId && { child_store_id: childStoreId }),
        comment:                  reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",
        special_request:          reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",
        notes:                    reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",
        round_off_details:        {
            round_off:     configRoundOff,
            type:          configType,
            payment_types: configPaymentTypes,
        },

        sub_total:                offerSubTotal,
        total_net_sale:           offerSubTotal,
        total_item_tax:           offerTax,
        total_gross_sale:         offerSubTotal + offerTax,
        transaction_grand_amount: offerGrand,
        grand_total:              offerGrand,
        rounded_total:            offerGrand,
        round_off_amount:         0,
        total_discount:           offerTotalDiscount,
        applied_discount:         offerAppliedDiscount,
        tip_amount:               tip,

        invoice_no:               invoiceNo,
        order_no:                 orderNo,
        offline_invoice_no:       offlineNo,

        item_details:             flatItems,
        customer_details:         customerDetails,
        table_details:            tableDetails || {},

        ...(charges.length > 0 && { charges }),
        additional_charges:       0,
        change_tender:            0,
        is_void:                  false,
        is_kot_printed:           false,
    };

    const calculatedPayload = executeCalculation(initialPayload, selectedTender, storeConfig);
    return { ...calculatedPayload, table_details: tableDetails || {} };
};

// ─────────────────────────────────────────────
// MAIN ENTRY POINT
// ─────────────────────────────────────────────

const placeOrder = async ({ flow, orderStatus, recalculatedTotals, paymentResp, activeOrder, paymentOption }) => {
    const state         = store.getState();
    const reduxSession  = state.sessionReducer  || {};
    const reduxCart     = state.cartReducer     || {};
    const reduxCustomer = state.customerReducer || {};

    const storeInfo    = reduxSession.store_details;
    const storeConfig  = reduxSession.store_config;
    const storeId      = reduxSession.store_id;
    const tableDetails = reduxSession.table_details;
    const customer     = reduxCustomer.customer || {};
    const customerMembership = reduxCustomer.customerMembership || [];

    // Canonical set of table IDs to clear from redux on completion
    const getTableIdsToClear = (order) => {
        const ids = new Set();
        if (tableDetails?.table_id) ids.add(String(tableDetails.table_id));
        if (tableDetails?.table_no) ids.add(String(tableDetails.table_no));
        if (order?.store_details?.table_id) ids.add(String(order.store_details.table_id));
        return [...ids];
    };

    // ── 1. FSR PAY-BILL ──────────────────────────────────────────
    if (flow === "FSR_PAY_BILL") {
        const fsrStatus  = orderStatus || "CLOSE";
        const transResp  = paymentResp?.paymentResponse || paymentResp || {};
        const offlineNo  = activeOrder?.offline_invoice_no || activeOrder?.offlineInvoiceNo || "";
        const invoiceNo  = activeOrder?.invoice_no         || activeOrder?.invoiceNo         || "";

        // When an offer was applied on the pay-bill screen, engineDiscount holds
        // the post-offer per-item state (discount_amount + applied_discount) and
        // the discounted grand total. Prefer that over the raw activeOrder.
        const engineDiscount = reduxCart?.engineDiscount;
        const engineHasOffer = engineDiscount
            && Array.isArray(engineDiscount.item_details)
            && engineDiscount.item_details.length > 0
            && (engineDiscount.total_discount || 0) > 0;

        const tip = parseFloat(reduxCart?.tip || 0);
        // Provisional amount for the initial payment_details stub. Engine
        // recomputes the authoritative transaction_grand_amount (folding tip
        // and applying round-off) further below — we overwrite payment amount
        // with that.
        const provisionalAmount = engineHasOffer && engineDiscount.transaction_grand_amount != null
            ? parseFloat(engineDiscount.transaction_grand_amount) + tip
            : (recalculatedTotals?.total || activeOrder?.total || 0) + tip;

        const baseItems = engineHasOffer
            ? engineDiscount.item_details
            : (activeOrder?.item_details || []);

        // Propagate invoice fields into every item and its addons
        const enrichedItems = Array.isArray(baseItems)
            ? baseItems.map(item => {
                const addonsSource = item.selectedAddonsItems || item.selected_addons_items || [];
                const cleanItem    = { ...item };
                delete cleanItem.selected_addons_items;
                return {
                    ...cleanItem,
                    invoice_no:         invoiceNo  || item.invoice_no  || "",
                    offline_invoice_no: offlineNo  || item.offline_invoice_no || "",
                    selectedAddonsItems: addonsSource.map(addon => ({
                        ...addon,
                        invoice_no:         invoiceNo || addon.invoice_no         || "",
                        offline_invoice_no: offlineNo || addon.offline_invoice_no || "",
                    })),
                };
            })
            : [];

        const gc            = storeConfig?.general_config || {};
        const configRoundOff = gc.round_off === true || gc.round_off === "true";
        const configType     = (gc.type || gc.round_off_type || "nearest").toLowerCase();
        const configPaymentTypes = gc.payment_types || gc.round_off_payments || [];

        const childStoreId = enrichedItems.find(i => i.store_id)?.store_id || reduxCart?.child_store_id || activeOrder?.child_store_id || null;

        const orderRaw = {
            ...activeOrder,
            order_state:        activeOrder?.order_state || "PLACED",
            order_status:       fsrStatus,
            invoice_no:         invoiceNo,
            offline_invoice_no: offlineNo,
            payment_details:    [{
                ...paymentOption,
                amount: provisionalAmount,
                transaction_id: transResp?.razorpay_payment_id || transResp?.TXNID || transResp?.transaction_id || "",
                payment_gateway: paymentOption?.payment_gateway || (transResp?.razorpay_payment_id ? "RAZORPAY" : (transResp?.TXNID ? "PAYTM" : "NONE")),
                transaction_details: {
                    
                    razorpay_payment_id: transResp?.razorpay_payment_id || "",
                    razorpay_order_id: transResp?.razorpay_order_id || "",
                    razorpay_signature: transResp?.razorpay_signature || "",
                    ...(transResp?.TXNID && {
                        TXNID: transResp.TXNID,
                        STATUS: transResp.STATUS || ""
                    })
                }
            }],
            sub_total:          engineHasOffer && engineDiscount.sub_total != null
                ? parseFloat(engineDiscount.sub_total)
                : (recalculatedTotals?.itemTotal ?? activeOrder?.sub_total),
            total_tax:          engineHasOffer && engineDiscount.total_item_tax != null
                ? parseFloat(engineDiscount.total_item_tax)
                : (recalculatedTotals?.tax ?? activeOrder?.total_tax),
            total_item_tax:     engineHasOffer && engineDiscount.total_item_tax != null
                ? parseFloat(engineDiscount.total_item_tax)
                : (recalculatedTotals?.tax ?? activeOrder?.total_item_tax),
            total_discount:     engineHasOffer
                ? parseFloat((engineDiscount.total_discount || 0).toFixed(2))
                : (recalculatedTotals?.discount ?? activeOrder?.total_discount),
            discount:           engineHasOffer
                ? parseFloat((engineDiscount.total_discount || 0).toFixed(2))
                : (recalculatedTotals?.discount ?? activeOrder?.discount),
            ...(engineHasOffer && { applied_discount: engineDiscount.applied_discount || [] }),
            tip:                tip,
            tip_amount:         tip,
            total:              provisionalAmount,
            grand_total:        provisionalAmount,
            rounded_total:      provisionalAmount,
            is_send:            true,
            is_kot_printed:     false,
            round_off_details:  {
                round_off:     configRoundOff,
                type:          configType,
                payment_types: configPaymentTypes,
            },
            item_details:       enrichedItems,
            ...(childStoreId && { child_store_id: childStoreId }),
        };

        console.log("FSR_PAY_BILL: input to CalculationEngine:", orderRaw);
        const updatedOrder = executeCalculation(orderRaw, paymentOption, storeConfig);
        console.log("FSR_PAY_BILL: output from CalculationEngine:", updatedOrder);

        // Sync order-level and payment amounts to the engine-authoritative
        // transaction_grand_amount (tip folded, round-off applied).
        const engineFinalAmount = parseFloat(
            updatedOrder?.transaction_grand_amount ?? provisionalAmount
        );
        updatedOrder.total         = engineFinalAmount;
        updatedOrder.grand_total   = engineFinalAmount;
        updatedOrder.rounded_total = engineFinalAmount;
        if (Array.isArray(updatedOrder.payment_details) && updatedOrder.payment_details.length > 0) {
            updatedOrder.payment_details = updatedOrder.payment_details.map((pd, idx) =>
                idx === 0 ? { ...pd, amount: engineFinalAmount } : pd
            );
        }

        const resp = await closeOrderViaSaveOrder({
            order:         updatedOrder,
            transResponse: transResp,
            orderStatus:   fsrStatus,
        });

        if (resp?.status) {
            // Backend response is authoritative for stamped totals. Fall back
            // to client-side updatedOrder only if the API didn't return data.
            const persistedOrder = resp?.data || updatedOrder;
            store.dispatch(order_place([persistedOrder]));
            store.dispatch(clear_last_order());
            getTableIdsToClear(activeOrder).forEach(tId =>
                store.dispatch({ type: "CLEAR_ACTIVE_TABLE_ORDER", payload: { tableNo: tId } })
            );
            store.dispatch(clearCart());
            store.dispatch(clear_applied_offer());
        }
        return resp;
    }

    // ── 2. FSR KOT / QSR CHECKOUT ───────────────────────────────
    if (flow === "FSR_KOT" || flow === "QSR_CHECKOUT") {
        const isKOT     = flow === "FSR_KOT";
        const tableNoStr = String(tableDetails?.table_id || tableDetails?.table_no || "").trim() || "default";

        // For KOT: fetch fresh order list to find existing open order at this table
        let currentActiveOrder  = null;
        let customer_membership = {};
        if (isKOT) {
            let freshOrderDetails = reduxCustomer?.last_order?.order_details || [];
            if (customer?.cust_mobile) {
                const freshResp = await get_customer_list({ cust_mobile: customer.cust_mobile });
                if (freshResp) {
                    store.dispatch(set_last_order(freshResp));
                    freshOrderDetails   = freshResp?.order_details || [];
                    customer_membership = freshResp?.membership    || {};
                }
            }

            const sessionDate = storeInfo?.business_date?.slice(0, 10) || "";
            currentActiveOrder = freshOrderDetails.find(order => {
                if (tableDetails?.table_id !== order?.store_details?.table_id) return false;
                if (sessionDate) {
                    const orderDate = (order?.business_date || "").slice(0, 10);
                    if (orderDate && orderDate !== sessionDate) return false;
                }
                return (order?.order_status || "").toUpperCase() === "OPEN";
            }) || activeOrder;

            if (
                currentActiveOrder?.is_void === true ||
                currentActiveOrder?.isVoid === true ||
                (currentActiveOrder?.order_status || "").toUpperCase() === "VOID"
            ) {
                currentActiveOrder = null;
            }
        }

        const isVoided = activeOrder?.is_void === true ||
                         activeOrder?.isVoid === true ||
                         (activeOrder?.order_status || "").toUpperCase() === "VOID";

        // Combine previously ordered items (KOT) with newly added cart items
        let savedItems = [];
        if (isKOT) {
            savedItems = Array.isArray(currentActiveOrder?.item_details)
                ? currentActiveOrder.item_details
                : (reduxCustomer.active_table_orders[tableNoStr]?.items || []);
        }
        const addedItems = reduxCart?.addedItems || [];

        // Resolve invoice numbers from the active order (if re-ordering on an existing KOT)
        // If brand new order, generate offline_invoice_no locally: order_source_code-invoice_no-timestamp
        const offlineNo = currentActiveOrder?.offline_invoice_no
            || currentActiveOrder?.offlineInvoiceNo
            || (isVoided ? null : (activeOrder?.offline_invoice_no || activeOrder?.offlineInvoiceNo))
            || `QR-1-${Date.now()}`;
        
        const invoiceNo = currentActiveOrder?.invoice_no
            || currentActiveOrder?.invoiceNo
            || (isVoided ? null : (activeOrder?.invoice_no || activeOrder?.invoiceNo))
            || "";

        // Build offer / discount structure
        const offer        = reduxCart?.appliedOffer;
        const appliedOffer = offer
            ? [{
                offer_details: {
                    entity:             offer?.entity,
                    is_source_item:     true,
                    mapped_offer_items: [],
                    offer_header: {
                        deal_type:        offer?.offer_header?.deal_type,
                        description:      offer?.offer_header?.description,
                        offer_header_id:  offer?.offer_header?.offer_header_id ?? 0,
                        terms_conditions: offer?.offer_header?.terms_conditions,
                    },
                    offer_id: offer?.offer_id ?? 0,
                },
                percentage: parseFloat(offer?.offer_term?.[0]?.discount_type_value) ?? 0,
                title:      "Order Level Discount",
                value:      parseFloat(reduxCart?.resultDis || 0),
            }]
            : [];

        // Saved items are already flat (from a prior save-order response) — do NOT re-transform.
        // Re-running transformItems on them corrupts fields the server has already normalized
        // (e.g. main_item_id gets dropped, item_id/sku emptied). Only flatten freshly added items,
        // and tell transformItems to continue seq numbering from the saved items' max so the
        // merged list stays unique and monotonic without mutating saved seqs.
        const flatSaved = isKOT ? savedItems : [];
        const maxSavedSeq = flatSaved.reduce(
            (max, it) => Math.max(max, parseInt(it?.item_seq, 10) || 0),
            0
        );
        const flatAdded = transformItems(addedItems, maxSavedSeq);
        const flatItems = [...flatSaved, ...flatAdded];
        const { subTotal, tax } = sumItemTotals(flatItems);
        const tip           = reduxCart?.tip || 0;
        const StoreCharge   = storeConfig?.general_config?.store_charges || reduxCustomer?.storeCharges || null;
        const charges       = buildCharges(flatItems, StoreCharge);
        const taxes         = buildTaxes(flatItems, storeInfo?.store_id);
        const selectedTender = reduxCart?.selectedTender || {};

        const order = reduxCart?.orderType?.order;

        // Ask the engine for the rounded total so payment_details.amount matches
        // what the customer paid (rounded), not raw subTotal + tax + tip.
        const selectedTenderCode = selectedTender?.tnd_code || paymentOption?.tnd_code || "";
        const childStoreId = flatItems.find(i => i.store_id)?.store_id || reduxCart?.child_store_id || null;
        const finalStoreName = (reduxSession.associated_stores || []).find(s => s.store_id === childStoreId)?.store_name || storeInfo?.store_name || "";

        // computeCheckoutTotals with applyCurrentOffer:true replays the
        // engineDiscount snapshot (post-offer) through refreshCalculation with
        // the current tip — same helper the display uses. When no offer is
        // applied, falls back to a clean engine calc.
        const engineTotals = computeCheckoutTotals(flatItems, {
            orderType:         order?.order_type || "Dine In",
            storeId:           storeInfo?.store_id || storeId,
            paymentOption:     { tnd_code: selectedTenderCode },
            storeConfig,
            itemsAreFlat:      true,
            tip,
            applyCurrentOffer: true,
        }) || {};
        const transaction_grand_amount = engineTotals.transaction_grand_amount || (subTotal + tax + tip);
        const roundOffAmt              = engineTotals.round_off_amount || 0;

        // When an offer is active, engineDiscount is authoritative for
        // discount totals + per-item discount_amount stamping. Save-order
        // payload mirrors what the customer saw in the UI.
        const engineDiscountSnap = reduxCart?.engineDiscount;
        const hasOffer = !!(engineDiscountSnap?.total_discount);
        const payloadSubTotal      = hasOffer ? (engineTotals.sub_total ?? subTotal) : subTotal;
        const payloadTax           = hasOffer ? (engineTotals.total_item_tax ?? tax) : tax;
        const payloadGross         = hasOffer ? (engineTotals.total_gross_sale ?? (payloadSubTotal + payloadTax)) : (subTotal + tax);
        const payloadTotalDiscount = hasOffer
            ? parseFloat((engineTotals.total_discount || engineDiscountSnap?.total_discount || 0).toFixed(2))
            : parseFloat((reduxCart?.resultDis || 0).toFixed(2));
        const payloadGrand         = hasOffer ? transaction_grand_amount : (subTotal + tax + tip);
        const payloadRoundedTotal  = hasOffer ? (engineTotals.rounded_total ?? transaction_grand_amount) : (subTotal + tax + tip);
        const payloadItemDetails   = hasOffer && Array.isArray(engineDiscountSnap?.item_details) && engineDiscountSnap.item_details.length
            ? engineDiscountSnap.item_details
            : flatItems;
        const payloadAppliedDiscount = hasOffer && Array.isArray(engineDiscountSnap?.applied_discount) && engineDiscountSnap.applied_discount.length
            ? engineDiscountSnap.applied_discount
            : appliedOffer;

        const paymentDetails = buildPaymentDetails({
            paymentResp,
            selectedTender,
            subTotal, tax, tip,
            charges, taxes,
            transaction_grand_amount,
        });

        const customerDetails = buildCustomerDetails(customer, customerMembership, customer_membership);

        const gc            = storeConfig?.general_config || {};
        const configRoundOff = gc.round_off === true || gc.round_off === "true";
        const configType     = (gc.type || gc.round_off_type || "nearest").toLowerCase();
        const configPaymentTypes = gc.payment_types || gc.round_off_payments || [];

        // QSR_CHECKOUT saves as CLOSE in a single call (payment already succeeded),
        // or as OPEN when the caller passed that in (e.g. Pay Later).
        // KOT saves as OPEN (no payment yet).
        const isQSRClose      = flow === "QSR_CHECKOUT";
        const effectiveStatus = isKOT ? "OPEN"
                              : isQSRClose ? (orderStatus || "CLOSE")
                              : orderStatus;
        const closePayments   = isQSRClose
            ? buildPaymentDetailsForClose(paymentResp, selectedTender, {
                transaction_grand_amount,
                round_off_amount: roundOffAmt,
            })
            : null;

        const round_off_details_close = isQSRClose ? {
            round_off:     configRoundOff,
            type:          configType,
            payment_types: configPaymentTypes,
        } : null;

        // Single payload object — all three flows (QSR / KOT new / KOT append) use this
        const initialPayload = {
            tent_id:                  reduxSession.tent_id || "",
            order_source_code:        "QR",
            order_source:            "QR Order",
            order_type:               order,
            order_status:             effectiveStatus,
            order_state:              currentActiveOrder?.order_state || (isVoided ? "PLACED" : (activeOrder?.order_state || "PLACED")),
            emp_no:                   storeInfo?.emp_no   || "",
            store_id:                 storeInfo?.store_id || storeId,
            ...(childStoreId && { child_store_id: childStoreId }),
            comment:                  reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",
            special_request:          reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",
            notes:                    reduxCart?.specialRequestText || reduxCart?.comment || reduxCart?.special_request || "",

            sub_total:                payloadSubTotal,
            total_net_sale:           payloadSubTotal,
            total_item_tax:           payloadTax,
            total_gross_sale:         payloadGross,
            transaction_grand_amount: payloadGrand,
            grand_total:              payloadGrand,
            rounded_total:            payloadRoundedTotal,
            round_off_amount:         roundOffAmt,
            total_discount:           payloadTotalDiscount,
            // Top-level discount_amount/discount_percent feed the engine's
            // per-item distribution (a2y at CalculationEngine-sharedLogic
            // line 1670-1677). Without them, executeCalculation below
            // recomputes total_discount as 0 and drops the offer.
            discount_amount:          hasOffer ? (engineDiscountSnap?.discount_amount || 0) : 0,
            discount_percent:         hasOffer ? (engineDiscountSnap?.discount_percent || 0) : 0,
            tip_amount:               tip,

            invoice_no:               invoiceNo,
            offline_invoice_no:       offlineNo,

            item_details:             payloadItemDetails,
            applied_discount:         payloadAppliedDiscount,
            customer_details:         customerDetails,
            table_details:            tableDetails || {},
            store_details: {
                integration:      storeInfo?.integration || "",
                store_name:       finalStoreName,
                store_id:         storeInfo?.store_id || storeId || "",
                store_ad1:        storeInfo?.store_ad1 || "",
                store_ad2:        storeInfo?.store_ad2 || "",
                store_city:       storeInfo?.store_city || "",
                store_contact_no: storeInfo?.store_contact_no || "",
                store_country:    storeInfo?.store_country || "",
                store_session_id: storeInfo?.store_session_id || "",
                store_zip:        storeInfo?.store_zip || "",
            },
            payment_details:          isKOT ? [] : (closePayments || paymentDetails),

            ...(isQSRClose && { round_off_details: round_off_details_close }),

            ...(charges.length > 0 && { charges }),
            additional_charges:       0,
            change_tender:            0,
            is_void:                  false,

            // KOT: append to existing order
            ...(isKOT && currentActiveOrder && {
                _id:                currentActiveOrder._id,
                order_id:           currentActiveOrder.order_id   || currentActiveOrder._id,
                id:                 currentActiveOrder.id         || currentActiveOrder._id,
                master_order_id:    currentActiveOrder.master_order_id,
                masterOrderId:      currentActiveOrder.masterOrderId || currentActiveOrder.master_order_id,
                order_no:           currentActiveOrder.order_no,
                orderNo:            currentActiveOrder.orderNo,
                invoice_no:         invoiceNo,
                offline_invoice_no: offlineNo,
            }),
        };

        console.log("Checkout/KOT: input to CalculationEngine:", initialPayload);
        // When an offer is active, computeCheckoutTotals above already ran the
        // engine on the post-offer snapshot. Skip the second executeCalculation
        // and merge engineTotals onto initialPayload — a second run would
        // require zeroing accumulators (see y2x doubling at
        // CalculationEngine-sharedLogic.mjs line 22545) and adds no info the
        // replay didn't already produce. For no-offer carts we still run the
        // engine here to get item-level tax breakdowns.
        const orderPayload = hasOffer
            ? initialPayload
            : {
                ...(executeCalculation(initialPayload, selectedTender, storeConfig) || initialPayload),
                table_details: tableDetails || {},
            };
        console.log("Checkout/KOT: payload for save-order:", orderPayload);

        const _resp = await create_order(orderPayload);
        if (_resp?.status) {
            let orderData = _resp.data;
            if (orderData && !Array.isArray(orderData)) {
                orderData = orderData.master ? [orderData.master] : [orderData];
            }
            const confirmedData = (orderData || []).map(o => ({ ...o, item_details: o.item_details || flatItems }));

            store.dispatch(order_place(confirmedData));

            if (isKOT) {
                const placedOrder = confirmedData?.[0] || confirmedData;
                if (placedOrder) {
                    store.dispatch({ type: "SET_ACTIVE_TABLE_ORDER", payload: { tableNo: tableNoStr, data: { order: placedOrder } } });
                    if (placedOrder.item_details) {
                        store.dispatch({ type: "SET_ACTIVE_TABLE_ORDER", payload: { tableNo: tableNoStr, data: { items: placedOrder.item_details } } });
                    }
                    store.dispatch({ type: "SET_OFFLINE_INVOICE_NO", offline_invoice_no: placedOrder?.offline_invoice_no || null });
                }
            }
        }
        return _resp;
    }
};

/**
 * Recalculate unit prices for cart items based on channel pricing for activeOrderCode.
 */
export const recalculateItemsForChannel = (addedItems, catalogByStore, activeOrderCode, defaultStoreId) => {
    if (!Array.isArray(addedItems) || addedItems.length === 0) return [];
    if (!activeOrderCode) return addedItems;

    return addedItems.map((cartItem) => {
        const storeId = cartItem.store_id || defaultStoreId;
        const storeCatalog = catalogByStore?.[String(storeId)] || {};
        const catalogItems = storeCatalog.items || [];

        // Match catalog item by _id, item_id, or sku
        const catalogItem = catalogItems.find((catItem) =>
            (catItem._id && (catItem._id === cartItem._id || catItem._id === cartItem.item_id)) ||
            (catItem.sku && cartItem.sku && catItem.sku === cartItem.sku) ||
            (catItem.item_id && cartItem.item_id && catItem.item_id === cartItem.item_id)
        );

        if (!catalogItem) return cartItem;

        // Find channel PLU for active order code
        const matchedChannelPlu = catalogItem.channel_plu?.find(
            (w) => w?.channel_code?.toLowerCase() === String(activeOrderCode).toLowerCase()
        );
        const channelValue = parseFloat(matchedChannelPlu?.plu || 0);

        // Find variant
        const cartVariant = cartItem.item_variant?.[0];
        const catalogVariant = (catalogItem.item_variant || []).find((v) =>
            (v.sku && cartVariant?.sku && v.sku === cartVariant.sku) ||
            (v._id && cartVariant?._id && v._id === cartVariant._id)
        ) || catalogItem.item_variant?.[0];

        const basePlu = parseFloat(catalogVariant?.plu ?? cartItem.regular_sales_unit_price ?? 0);
        const updatedPlu = parseFloat((basePlu + channelValue).toFixed(2));
        const itemQty = parseInt(cartItem.quantity || cartItem.qty || 1, 10);
        const updatedExtAmount = parseFloat((updatedPlu * itemQty).toFixed(2));

        return {
            ...cartItem,
            regular_sales_unit_price: updatedPlu,
            extended_amount: updatedExtAmount,
            total_amount: updatedExtAmount,
            sub_total: updatedExtAmount,
            item_variant: (cartItem.item_variant || []).map((v, i) =>
                i === 0 ? { ...v, plu: updatedPlu } : v
            ),
        };
    });
};

// ─────────────────────────────────────────────
// CART CALCULATION MIDDLEWARE
// Triggered on every cart mutation to keep cart totals in sync.
// ─────────────────────────────────────────────

export const calculationMiddleware = (storeApi) => (next) => async (action) => {
    const result = next(action);
    const triggerActions = [
        "ADD_TO_CART", "REMOVE_ITEM", "SUB_QUANTITY", "ADD_QUANTITY",
        "DISCOUNT", "REMOVE_DISCOUNT", "TIP", "SPECIAL_REQUEST",
        "UPDATE_ITEM_SPECIAL_REQUEST", "CLEAR_CART", "SET_ORDER_TYPE", "UPDATE_CART_ITEM_PRICES"
    ];

    if (triggerActions.includes(action.type)) {
        try {
            const reduxState    = storeApi.getState();
            const reduxCart     = reduxState.cartReducer;
            const storeConfig   = reduxState.sessionReducer?.store_config || {};
            const gc            = storeConfig?.general_config || {};
            const isRoundOff    = gc.round_off === true || gc.round_off === "true";
            const roundOffType  = (gc.type || gc.round_off_type || "nearest").toLowerCase();
            const applicablePaymentTypes = Array.isArray(gc.payment_types)
                ? gc.payment_types
                : Array.isArray(gc.round_off_payments) ? gc.round_off_payments : [];

            const item_details = transformItems(reduxCart?.addedItems || []);
            const activeOrderTypeObj = reduxCart?.orderType?.order;
            const activeOrderType = activeOrderTypeObj?.order_type || activeOrderTypeObj?.order_code || "Dine In";

            const payload = {
                item_details,
                tip_amount: reduxCart?.tip || 0,
                order_type: activeOrderType,
            };

            const isTaxInclusive = gc.item_price_contains_tax === "Include";

            console.log("[middleware engine IN]", {
                isRoundOff, roundOffType, applicablePaymentTypes, isTaxInclusive,
                payload: JSON.parse(JSON.stringify(payload)),
                items: item_details.map(i => ({
                    name: i.item_name,
                    qty: i.quantity,
                    unit: i.regular_sales_unit_price,
                    ext: i.extended_amount,
                    main_item_id: i.main_item_id,
                    sku: i.sku,
                    variant_name: i.variant_name,
                })),
            });

            const cleanEnginePayload = { ...payload };
            if (cleanEnginePayload.invoice_no === "" || cleanEnginePayload.invoice_no == null || isNaN(Number(cleanEnginePayload.invoice_no))) {
                delete cleanEnginePayload.invoice_no;
            }
            if (cleanEnginePayload.offline_invoice_no === "" || cleanEnginePayload.offline_invoice_no == null) {
                delete cleanEnginePayload.offline_invoice_no;
            }

            const calculatedStr = refreshCalculationJs(
                JSON.stringify(cleanEnginePayload),
                false, 0, 0, "", isRoundOff, roundOffType,
                applicablePaymentTypes,
                false, isTaxInclusive
            );

            if (calculatedStr) {
                const parsed = JSON.parse(calculatedStr);
                console.log("[middleware engine OUT]", {
                    sub_total: parsed.sub_total,
                    total_item_tax: parsed.total_item_tax,
                    total_net_sale: parsed.total_net_sale,
                    total_gross_sale: parsed.total_gross_sale,
                    transaction_grand_amount: parsed.transaction_grand_amount,
                    rounded_total: parsed.rounded_total,
                    round_off_amount: parsed.round_off_amount,
                    items: parsed.item_details?.map(i => ({
                        name: i.item_name,
                        qty: i.quantity,
                        unit: i.regular_sales_unit_price,
                        ext: i.extended_amount,
                        sub_total: i.sub_total,
                        tax: i.total_item_tax,
                        gross: i.total_gross_sale,
                        main_item_id: i.main_item_id,
                        sku: i.sku,
                    })),
                });
                storeApi.dispatch({ type: "SYNC_CART_TOTALS", payload: parsed });
            }
        } catch (err) {
            console.error("Calculation Middleware Error:", err, err?.stack);
        }
    }
    return result;
};

export {
    placeOrder,
    buildSaveOrderPayload,
    get_table_orders,
    get_order_type,
    send_order_receipt,
    download_invoice,
    create_order,
    closeOrderViaSaveOrder,
    get_banner_detial,
    computeCheckoutTotals,
    executeCalculation as executeCalculationEngine,
};